import type { WorkArea } from "./windowGeometry";

export type IconGrid = { columns: number; rows: number; capacity: number; stepX: number; stepY: number; padding: number; iconWidth: number; iconHeight: number };
export type IconSlot = { column: number; row: number; page: number };
export type IconPosition = { x: number; y: number; page: number };
export type IconPositions = Record<string, IconPosition>;

export function iconGrid(area: WorkArea, density: "comfortable" | "compact" = "comfortable"): IconGrid {
  const padding = Math.min(16, Math.max(0, (area.width - 48) / 2), Math.max(0, (area.height - 70) / 2));
  const width = Math.max(1, area.width - padding * 2);
  const height = Math.max(1, area.height - padding * 2 - 40);
  const step = density === "compact" ? 84 : 96;
  const size = density === "compact" ? 76 : 88;
  const stepX = Math.min(step, width);
  const stepY = Math.min(step, height);
  const columns = Math.max(1, Math.floor(width / stepX));
  const rows = Math.max(1, Math.floor(height / stepY));
  return { columns, rows, capacity: columns * rows, stepX, stepY, padding, iconWidth: Math.min(size, stepX), iconHeight: Math.min(size, stepY) };
}

export function slotAt(index: number, grid: IconGrid): IconSlot {
  const local = index % grid.capacity;
  return { page: Math.floor(index / grid.capacity), column: Math.floor(local / grid.rows), row: local % grid.rows };
}

export function slotIndex(slot: IconSlot, grid: IconGrid) { return slot.page * grid.capacity + slot.column * grid.rows + slot.row; }
export function encodeSlot(slot: IconSlot, grid: IconGrid): IconPosition {
  return { x: grid.columns > 1 ? slot.column / (grid.columns - 1) : 0, y: grid.rows > 1 ? slot.row / (grid.rows - 1) : 0, page: slot.page };
}

/** Saved locations reserve cells first; collisions and new icons use the next free cell. */
export function resolveIconSlots(ids: string[], positions: IconPositions, grid: IconGrid): Record<string, IconSlot> {
  const result: Record<string, IconSlot> = {};
  const used = new Set<number>();
  const maximumPage = Math.max(0, Math.ceil(ids.length / grid.capacity) - 1);
  const pending: string[] = [];
  for (const id of ids) {
    const saved = positions[id];
    if (!saved) { pending.push(id); continue; }
    const slot = { column: Math.round(saved.x * (grid.columns - 1)), row: Math.round(saved.y * (grid.rows - 1)), page: Math.min(saved.page, maximumPage) };
    const index = slotIndex(slot, grid);
    if (used.has(index)) pending.push(id);
    else { used.add(index); result[id] = slot; }
  }
  let next = 0;
  for (const id of pending) {
    while (used.has(next)) next += 1;
    result[id] = slotAt(next, grid);
    used.add(next);
  }
  return result;
}

export function moveIcon(id: string, target: IconSlot, slots: Record<string, IconSlot>, grid: IconGrid): IconPositions {
  const source = slots[id];
  const result = Object.fromEntries(Object.entries(slots).map(([key, slot]) => [key, encodeSlot(slot, grid)]));
  if (!source) return result;
  const safe = { page: Math.max(0, Math.floor(target.page)), column: Math.max(0, Math.min(grid.columns - 1, target.column)), row: Math.max(0, Math.min(grid.rows - 1, target.row)) };
  const occupied = Object.keys(slots).find((key) => key !== id && slotIndex(slots[key], grid) === slotIndex(safe, grid));
  if (occupied) result[occupied] = encodeSlot(source, grid);
  result[id] = encodeSlot(safe, grid);
  return result;
}

export function iconLayoutKey(userId: string) { return `base-sul-os:desktop-icons:v1:${userId}`; }

export function readIconPositions(userId: string, storage?: Pick<Storage, "getItem">): IconPositions {
  if (!userId) return {};
  try {
    const parsed = JSON.parse((storage ?? window.localStorage).getItem(iconLayoutKey(userId)) ?? "null");
    if (parsed?.version !== 1 || !parsed.positions || typeof parsed.positions !== "object" || Array.isArray(parsed.positions)) return {};
    return Object.fromEntries(Object.entries(parsed.positions).slice(0, 5000).filter(([id, item]) => {
      const value = item as Partial<IconPosition> | null;
      return /^(app|file):/.test(id) && id.length <= 512 && value && typeof value === "object"
        && typeof value.x === "number" && Number.isFinite(value.x) && value.x >= 0 && value.x <= 1
        && typeof value.y === "number" && Number.isFinite(value.y) && value.y >= 0 && value.y <= 1
        && typeof value.page === "number" && Number.isInteger(value.page) && value.page >= 0 && value.page <= 5000;
    })) as IconPositions;
  } catch { return {}; }
}

export function writeIconPositions(userId: string, positions: IconPositions, storage?: Pick<Storage, "setItem">) {
  if (!userId) return;
  try { (storage ?? window.localStorage).setItem(iconLayoutKey(userId), JSON.stringify({ version: 1, positions })); } catch { /* Positioning still works when local persistence is unavailable. */ }
}
